'use client';

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { Link, useRouter, usePathname } from '@/i18n/navigation';
import Image from 'next/image';

// ─── Constants ───────────────────────────────────────────────

const LOGO_SRC = '/design-images/logos/castle-hotel-daniel.svg';

/** Color schemes for the scrolled desktop header bar */
const SCROLLED_SCHEMES = [
  { name: 'Espresso',         bg: '#3b1a10', accent: '#d4a054', text: '#f5e6d0', divider: '#d4a054' },
  { name: 'Espresso Rosso',   bg: '#451812', accent: '#e0a850', text: '#f8ead4', divider: '#e0a850' },
  { name: 'Espresso Crema',   bg: '#3a1c12', accent: '#c89a4a', text: '#f0ddc0', divider: '#c89a4a' },
  { name: 'Espresso Scuro',   bg: '#2e1410', accent: '#d4a054', text: '#f2e4cc', divider: '#d4a054' },
  { name: 'Espresso Caldo',   bg: '#421a0e', accent: '#daa858', text: '#faecd6', divider: '#daa858' },
  { name: 'Espresso Rame',    bg: '#3c1610', accent: '#c8884a', text: '#f0e0c8', divider: '#c8884a' },
  { name: 'Espresso Mattone', bg: '#481c14', accent: '#d0a060', text: '#f5e8d2', divider: '#d0a060' },
  { name: 'Espresso Ruggine', bg: '#3e1810', accent: '#cc9448', text: '#eee0c4', divider: '#cc9448' },
  { name: 'Espresso Mogano',  bg: '#5e0e1a', accent: '#d8ac5c', text: '#f8f0f0', divider: '#d8ac5c' },
  { name: 'Espresso Fuoco',   bg: '#4a1a10', accent: '#dca050', text: '#f8ecd8', divider: '#dca050' },
];

/** Spa-specific color scheme — teal tones matching the footer spa variant */
const SPA_SCHEME = { name: 'Cellarium Teal', bg: '#2a5252', accent: '#8ed4d4', text: '#f0f8f8', divider: '#8ed4d4' };

type HeaderVariant = 'default' | 'spa';

/** Title fonts — arrow keys cycle through them */
const TITLE_FONT = 'var(--font-tex-gyre-pagella), serif';

/** Build translated nav items — must be called inside a component */
function useNavItems() {
  const t = useTranslations('common');
  return [
    { label: t('nav.rooms'), href: '/rooms' },
    { label: t('nav.dining'), href: '/dining' },
    { label: t('nav.spa'), href: '/spa' },
    { label: t('nav.activities'), href: '/activities' },
    { label: t('nav.about'), href: '/about-us' },
    { label: t('nav.corporate'), href: '/corporate' },
    { label: t('nav.events'), href: '/events' },
    { label: t('nav.newsAndOffers'), href: '/vouchers-and-offers' },
  ];
}

// ─── Types ───────────────────────────────────────────────────

interface SiteHeaderProps {
  logoSrc?: string;
  hideLogo?: boolean;
  hideGradient?: boolean;
  /** Desktop only: force the scrolled appearance (solid bg + text title) even at top of page */
  alwaysScrolled?: boolean;
  /** Show the DANIEL CASTLE text title even when hideLogo is true */
  showTitle?: boolean;
  /** Color variant — 'spa' switches to teal tones */
  variant?: HeaderVariant;
}

interface NavItem {
  label: string;
  href: string;
}

interface VariantProps {
  scrolled: boolean;
  mobileOpen: boolean;
  toggleMobile: () => void;
  logoSrc: string;
  hideLogo?: boolean;
  hideGradient?: boolean;
  alwaysScrolled?: boolean;
  showTitle?: boolean;
  schemeIndex: number;
  titleFont: string;
  navItems: NavItem[];
  variant?: HeaderVariant;
}

// ─── Logo Background ────────────────────────────────────────

function LogoBg({ children, visible = true }: { children: React.ReactNode; visible?: boolean }) {
  return (
    <div className="relative">
      <div
        className={`absolute -left-3 -right-3 -bottom-2 bg-white/20 rounded-br-[2rem] pointer-events-none transition-opacity duration-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ top: '-100vh' }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

// ─── Scroll Hook ─────────────────────────────────────────────

function useScrolled(threshold = 50) {
  const [scrolled, setScrolled] = useState(false);
  const didHydrate = useRef(false);

  // Two-phase hydration:
  // Phase 1 (first run): set the correct scrolled state — triggers synchronous re-render.
  // Phase 2 (re-run after state change): remove the pre-hydration style tag.
  // Both phases run before the browser paints, so the user never sees the wrong state.
  useLayoutEffect(() => {
    if (!didHydrate.current) {
      didHydrate.current = true;
      setScrolled(window.scrollY > threshold);
      return;
    }
    // After React has re-rendered with the correct state, remove the inline hint
    document.getElementById('__hs')?.remove();
  }, [threshold, scrolled]);

  useEffect(() => {
    const onScroll = () => {
      const isScrolled = window.scrollY > threshold;
      setScrolled(isScrolled);
      // Persist scroll state so the inline script in layout.tsx can hide the header
      // before React hydrates on the next page load, preventing a flash.
      try {
        if (isScrolled) sessionStorage.setItem('__hs', '1');
        else sessionStorage.removeItem('__hs');
      } catch { /* sessionStorage may be unavailable */ }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Set initial sessionStorage state
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return scrolled;
}

// ─── Language Switcher ───────────────────────────────────────

// RO/HU carry an "AI assisted" tag while their translations are still being
// refined by a human editor; drop the tag (or the whole `tag` field) per-locale
// once a translation reaches a quality we're happy to stand behind without it.
// EN is the brand language — no tag.
const LOCALES = [
  { code: 'en', label: 'EN', tag: '' },
  { code: 'ro', label: 'RO', tag: 'AI assisted' },
  { code: 'hu', label: 'HU', tag: 'AI assisted' },
] as const;

function LanguageSwitcher({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const switchLocale = useCallback((newLocale: string) => {
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${60 * 60 * 24 * 365}`;
    router.replace(pathname, { locale: newLocale as 'en' | 'ro' | 'hu' });
    setOpen(false);
  }, [router, pathname]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`} style={style}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm font-medium opacity-80 hover:opacity-100 transition-opacity"
      >
        <Globe className="w-4 h-4" />
        {locale.toUpperCase()}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border py-1 min-w-max z-50">
          {LOCALES.filter(l => l.code !== locale).map(l => (
            <button
              key={l.code}
              onClick={() => switchLocale(l.code)}
              className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left whitespace-nowrap"
            >
              <span className="font-medium">{l.label}</span>
              {l.tag && <span className="text-gray-400 text-xs">* {l.tag}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Inline language buttons for the mobile overlay (no dropdown) */
function MobileLanguageSwitcher({ variant = 'default' }: { variant?: HeaderVariant }) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();

  const switchLocale = useCallback((newLocale: string) => {
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${60 * 60 * 24 * 365}`;
    router.replace(pathname, { locale: newLocale as 'en' | 'ro' | 'hu' });
  }, [router, pathname]);

  const activeColor = variant === 'spa' ? 'text-[#8ed4d4]' : 'text-[#d4b896]';

  return (
    <div className="flex flex-col gap-2.5 px-6 pt-6">
      <Globe className="w-4 h-4 text-white/50" />
      {LOCALES.map(l => (
        <button
          key={l.code}
          onClick={() => l.code !== locale && switchLocale(l.code)}
          className={`text-sm font-medium text-left transition-colors ${
            l.code === locale
              ? activeColor
              : 'text-white/60 hover:text-white/90'
          }`}
        >
          {l.label}
          {l.tag && <span className="text-xs opacity-60"> * {l.tag}</span>}
        </button>
      ))}
    </div>
  );
}

// ─── Mobile Menu Overlay ─────────────────────────────────────

export function MobileOverlay({
  isOpen,
  onClose,
  logoSrc: overrideLogo,
  navItems,
  variant = 'default',
}: {
  isOpen: boolean;
  onClose: () => void;
  logoSrc?: string;
  navItems?: NavItem[];
  variant?: HeaderVariant;
}) {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 z-[60] flex h-full w-[200px] max-w-[60vw] flex-col overflow-y-auto shadow-2xl transition-transform duration-500 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: variant === 'spa' ? '#1a2e2e' : '#1a1010' }}
      >
        {/* Close button — aligned exactly with the hamburger */}
        <button
          onClick={onClose}
          className="fixed top-4 right-4 z-[61] flex items-center justify-center w-11 h-11 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-white/80 hover:text-white transition-all duration-300"
        >
          <X className="w-5 h-5" />
        </button>
        <nav className="flex flex-col px-6 pt-16">
          {(navItems || []).map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`py-2.5 text-base font-poppins tracking-wide border-b text-white/85 border-white/10 transition-colors ${
                variant === 'spa' ? 'hover:text-[#8ed4d4]' : 'hover:text-[#d4b896]'
              }`}
              onClick={onClose}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <MobileLanguageSwitcher variant={variant} />
        {/* Logo sits in normal flow with mt-auto so it pins to the bottom when
            there's spare height but flows below the language switcher (never
            overlapping it) on short viewports. */}
        <div className="mt-auto px-8 pb-10 pt-8 text-center">
          <Link href="/" onClick={onClose}>
            <Image
              src={overrideLogo || LOGO_SRC}
              alt="Daniel Castle Hotel"
              width={72}
              height={95}
              className="mx-auto opacity-20"
            />
          </Link>
        </div>
      </div>
    </>
  );
}

// ─── Floating Mobile Hamburger ────────────────────────────────
// Shows only on mobile when scrolled — replaces the full-width bar
// with a subtle floating button in the top-right corner.

export function FloatingMobileHamburger({
  onClick,
}: {
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="fixed top-4 right-4 z-[52] md:hidden flex items-center justify-center w-11 h-11 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-white/80 hover:text-white transition-all duration-500"
      aria-label="Open menu"
    >
      <Menu className="w-5 h-5" />
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// V1 — LEFT LOGO: CLASSIC REPLACE
// Logo replaces the text on the left. On scroll, logo fades
// out and text fades in. Clean and traditional.
// ═════════════════════════════════════════════════════════════

function NavV1({ scrolled, mobileOpen, toggleMobile, logoSrc, hideLogo, hideGradient, alwaysScrolled, showTitle, schemeIndex, titleFont, navItems, variant = 'default' }: VariantProps) {
  // On desktop, alwaysScrolled forces the scrolled appearance regardless of scroll position
  const desktopScrolled = alwaysScrolled || scrolled;
  // Show the left-side link block when logo is visible OR title is explicitly requested
  const showLeftBlock = !hideLogo || showTitle;
  const scheme = variant === 'spa' ? SPA_SCHEME : (SCROLLED_SCHEMES[schemeIndex] || SCROLLED_SCHEMES[0]);
  const [isMidUp, setIsMidUp] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    setIsMidUp(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMidUp(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <>
      <header
        data-site-header
        className={`fixed top-0 left-0 right-0 z-50 transition-all max-md:duration-300 md:duration-700 ${
          scrolled
            ? 'max-md:opacity-0 max-md:pointer-events-none'
            : hideGradient ? 'max-md:bg-transparent' : 'max-md:bg-gradient-to-b max-md:from-black/60 max-md:to-transparent'
        } ${
          desktopScrolled
            ? ''
            : hideGradient ? 'md:bg-transparent' : 'md:bg-gradient-to-b md:from-black/60 md:to-transparent'
        }`}
        style={desktopScrolled && isMidUp ? { backgroundColor: scheme.bg, transition: 'background-color 0.5s ease' } : undefined}
      >
        <div className="px-8 md:px-4 xl:px-6 2xl:px-16 min-[1700px]:px-[135px]">
          <div
            className={`flex items-center justify-between md:transition-all md:duration-500 ${
              desktopScrolled ? 'md:h-16' : hideLogo ? 'md:h-16' : 'md:py-0 md:h-44'
            } ${
              hideLogo ? 'h-16' : 'py-3'
            }`}
          >
            {showLeftBlock && (
            <Link href="/" className="relative flex items-center shrink-0">
              {/* Logo — visible when not scrolled (hidden entirely when hideLogo) */}
              {!hideLogo && (
              <div
                className={`transition-opacity duration-700 ${
                  desktopScrolled
                    ? 'md:opacity-0 md:absolute md:pointer-events-none'
                    : 'md:opacity-100 md:relative'
                } max-md:opacity-100 max-md:relative`}
              >
                <LogoBg visible={!desktopScrolled}>
                <Image
                  src={logoSrc}
                  alt="Daniel Castle Hotel"
                  width={115}
                  height={152}
                  className="h-24 lg:h-36 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
                  priority
                />
                </LogoBg>
              </div>
              )}
              {/* Text — fades in on scroll, or always visible when showTitle + hideLogo */}
              <div
                style={{ fontFamily: titleFont }}
                className={`font-bold md:text-base xl:text-lg 2xl:text-xl tracking-[0.2em] whitespace-nowrap transition-all duration-700 ${
                  hideLogo && showTitle
                    ? 'md:opacity-100 md:translate-x-0 md:relative max-md:opacity-0 max-md:absolute max-md:pointer-events-none'
                    : `${desktopScrolled
                        ? 'md:opacity-100 md:translate-x-0 md:relative'
                        : 'md:opacity-0 md:-translate-x-4 md:absolute md:left-0 md:pointer-events-none'
                      } max-md:opacity-0 max-md:-translate-x-4 max-md:absolute max-md:left-0 max-md:pointer-events-none`
                }`}
              >
                <span style={desktopScrolled ? { color: scheme.text } : undefined} className={desktopScrolled ? '' : 'text-white'}>DANIEL CASTLE</span>
              </div>
            </Link>
            )}

            <nav className={`hidden xl:flex items-center border-b-2 transition-all duration-700 ${
                  desktopScrolled ? 'border-transparent pb-0' : 'border-white/30 pb-1'
                } ${!showLeftBlock ? 'ml-auto' : ''}`}
                  style={{ gap: 'clamp(1rem, 3vw, 3rem)' }}>
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="font-inter xl:text-sm 2xl:text-base tracking-wide uppercase transition-colors duration-400"
                  style={{
                    color: desktopScrolled ? scheme.text : '#ffffff',
                    textShadow: desktopScrolled ? 'none' : '1px 1px 4px rgba(0, 0, 0, 0.5)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = scheme.accent)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = desktopScrolled ? scheme.text : '#ffffff')}
                >
                  {item.label}
                </Link>
              ))}
              <LanguageSwitcher
                className={desktopScrolled ? '' : 'text-white'}
                style={desktopScrolled ? { color: scheme.text } : undefined}
              />
            </nav>

            {/* Mid-range hamburger: visible md–lg only */}
            <button
              onClick={toggleMobile}
              className="hidden md:flex xl:hidden items-center justify-center w-10 h-10 rounded-full transition-colors duration-300 hover:bg-white/10"
              style={{ color: desktopScrolled ? scheme.text : '#ffffff' }}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>

          </div>
        </div>
        <div
          className={`hidden md:block h-[1px] transition-all duration-500 ${
            desktopScrolled ? '' : 'bg-transparent'
          }`}
          style={desktopScrolled ? {
            background: `linear-gradient(to right, transparent, ${scheme.divider}40, transparent)`,
          } : undefined}
        />
      </header>
      <MobileOverlay isOpen={mobileOpen} onClose={toggleMobile} logoSrc={logoSrc} navItems={navItems} variant={variant} />
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═════════════════════════════════════════════════════════════

export function SiteHeader({ logoSrc, hideLogo, hideGradient, alwaysScrolled, showTitle, variant = 'default' }: SiteHeaderProps = {}) {
  const scrolled = useScrolled();
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobile = () => setMobileOpen((prev) => !prev);
  const effectiveLogoSrc = logoSrc || LOGO_SRC;
  const navItems = useNavItems();

  // Locked to Espresso Mogano (v7)
  const schemeIndex = 8;

  return (
    <>
      <NavV1 scrolled={scrolled} mobileOpen={mobileOpen} toggleMobile={toggleMobile} logoSrc={effectiveLogoSrc} hideLogo={hideLogo} hideGradient={hideGradient} alwaysScrolled={alwaysScrolled} showTitle={showTitle} schemeIndex={schemeIndex} titleFont={TITLE_FONT} navItems={navItems} variant={variant} />
      <FloatingMobileHamburger onClick={toggleMobile} />
    </>
  );
}
